import React from 'react';
import {
  Weekend,
  Chair,
  TableRestaurant,
  Bed,
  Kitchen,
  Tv,
  Light,
  FormatPaint,
  DesignServices,
  GridOn,
  Curtains,
  Coffee,
  Desk,
  Carpenter,
  Build,
  Texture,
  KingBed,
  Hotel,
  OutdoorGrill,
  FilterFrames,
} from '@mui/icons-material';
import { Lightbulb, SofaIcon } from 'lucide-react';

export interface CategoryItem {
  id: number;
  parentId?: number | null;
  name: string;
  icon?: React.ReactNode; // for MUI icons
  image?: string;         // for custom images, e.g. "/icons/villa.png"
}







/* =========================================================
   1️⃣ TOP LEVEL CATEGORIES
========================================================= */

export const idFurnitureCategories: CategoryItem[] = [
  { id: 70000001, name: 'Living Room', icon: <SofaIcon />, image: '/icons/furniture/sofa.png' },
  { id: 70000002, name: 'Bedroom', icon: <Bed />, image: '/icons/furniture/bed.png' },
  { id: 70000003, name: 'Dining Room', icon: <TableRestaurant />, image: '/icons/furniture/dining.png' },
  { id: 70000004, name: 'Office & Study', icon: <Desk />, image: '/icons/furniture/office.png' },
  { id: 70000005, name: 'Outdoor & Garden', icon: <Weekend />, image: '/icons/furniture/outdoorfurniture.png' },
  { id: 70000006, name: 'Kitchen & Pantry', icon: <Kitchen />, image: '/icons/furniture/kitchen.png' },
  { id: 70000007, name: 'Lighting', icon: <Lightbulb />, image: '/icons/furniture/lighting.png' },
  { id: 70000008, name: 'Decor & Accessories', icon: <DesignServices />, image: '/icons/furniture/decor.png' },
  { id: 70000009, name: 'Storage & Organization', icon: <GridOn />, image: '/icons/furniture/StorageCabinets.png' },
  { id: 70000010, name: 'Kids & Nursery', icon: <Carpenter />, image: '/icons/furniture/babycot.png' },
  { id: 70000011, name: 'Custom / Bespoke', icon: <Build /> }, // no matching image yet
];

/* =========================================================
   2️⃣ SUBCATEGORIES
========================================================= */

export const idFurnitureSubcategories: CategoryItem[] = [
  { id: 70000101, parentId: 70000001, name: 'Sofas & Sectionals', icon: <SofaIcon />, image: '/icons/furniture/sofa.png' },
  { id: 70000102, parentId: 70000001, name: 'Coffee Tables', icon: <Coffee />, image: '/icons/furniture/coffeetable.png' },
  { id: 70000103, parentId: 70000001, name: 'TV Stands', icon: <Tv />, image: '/icons/furniture/tvstand.png' },
  { id: 70000104, parentId: 70000001, name: 'Accent Chairs', icon: <Chair /> }, // no matching image yet

  { id: 70000201, parentId: 70000002, name: 'Beds', icon: <Bed />, image: '/icons/furniture/bedframe.png' },
  { id: 70000202, parentId: 70000002, name: 'Mattresses', icon: <KingBed />, image: '/icons/furniture/matress.png' },

  { id: 70000301, parentId: 70000003, name: 'Dining Tables', icon: <TableRestaurant />, image: '/icons/furniture/dining.png' },

  { id: 70000401, parentId: 70000004, name: 'Desks', icon: <Desk />, image: '/icons/furniture/desk.png' },

  { id: 70000501, parentId: 70000005, name: 'Outdoor Sofas & Sets', icon: <OutdoorGrill />, image: '/icons/furniture/outdoorchair.png' },

  { id: 70000701, parentId: 70000007, name: 'Ceiling Lights', icon: <Lightbulb />, image: '/icons/furniture/lighting.png' },
  { id: 70000702, parentId: 70000007, name: 'Floor Lamps', icon: <Lightbulb />, image: '/icons/furniture/floorlamp.png' },
  { id: 70000703, parentId: 70000007, name: 'Table Lamps', icon: <Lightbulb />, image: '/icons/furniture/tablelamp.png' },

  { id: 70000601, parentId: 70000006, name: 'Kitchen Cabinets', icon: <Kitchen />, image: '/icons/furniture/kitchencabinet.png' },
  { id: 70000602, parentId: 70000006, name: 'Kitchen Islands', icon: <TableRestaurant />, image: '/icons/furniture/kitchen.png' },

  { id: 70000801, parentId: 70000008, name: 'Wall Art', icon: <FormatPaint />, image: '/icons/furniture/wallart.png' },
  { id: 70000803, parentId: 70000008, name: 'Mirrors', icon: <FilterFrames />, image: '/icons/furniture/mirror.png' },

  { id: 70000901, parentId: 70000009, name: 'Storage Cabinets', icon: <GridOn />, image: '/icons/furniture/StorageCabinets.png' },
  { id: 70000902, parentId: 70000009, name: 'Shoe Racks', icon: <GridOn />, image: '/icons/furniture/shoestorage.png' },

  { id: 70001001, parentId: 70000010, name: 'Kids Beds', icon: <KingBed />, image: '/icons/furniture/doubledecker.png' },
  { id: 70001003, parentId: 70000010, name: 'Kids Storage', icon: <GridOn />, image: '/icons/furniture/kidstorage.png' },

  { id: 70001101, parentId: 70000011, name: 'Custom Sofas', icon: <SofaIcon />, image: '/icons/furniture/sofa.png' },
  { id: 70001102, parentId: 70000011, name: 'Custom Wardrobes', icon: <Carpenter /> }, // no matching image yet
];





/* =========================================================
   3️⃣ PRODUCTS — FULL EXPANDED (ALL MERGED)
========================================================= */

export const idFurnitureProducts: CategoryItem[] = [

/* ================= SOFAS ================= */

{ id: 70010101, parentId: 70000101, name: 'Fabric Sofa', icon: <Texture /> },
{ id: 70010102, parentId: 70000101, name: 'Leather Sofa', icon: <Hotel /> },
{ id: 70010103, parentId: 70000101, name: 'Recliner Sofa', icon: <SofaIcon /> },
{ id: 70010104, parentId: 70000101, name: 'Single Recliner Chair', icon: <Chair /> },
{ id: 70010105, parentId: 70000101, name: 'Corner Sofa', icon: <SofaIcon /> },
{ id: 70010106, parentId: 70000101, name: 'L-Shaped Sofa', icon: <SofaIcon /> },
{ id: 70010107, parentId: 70000101, name: 'U-Shaped Sofa', icon: <SofaIcon /> },
{ id: 70010108, parentId: 70000101, name: 'Modular Sofa', icon: <SofaIcon /> },
{ id: 70010109, parentId: 70000101, name: 'Chaise Lounge Sofa', icon: <SofaIcon /> },
{ id: 70010110, parentId: 70000101, name: 'Sofa Bed / Sleeper Sofa', icon: <Hotel /> },
{ id: 70010111, parentId: 70000101, name: 'Loveseat (2-Seater)', icon: <SofaIcon /> },
{ id: 70010112, parentId: 70000101, name: '3-Seater Sofa', icon: <SofaIcon /> },
{ id: 70010113, parentId: 70000101, name: '4-Seater Sofa', icon: <SofaIcon /> },
{ id: 70010114, parentId: 70000101, name: 'Tufted Sofa', icon: <Texture /> },
{ id: 70010115, parentId: 70000101, name: 'Chesterfield Sofa', icon: <Texture /> },
{ id: 70010116, parentId: 70000101, name: 'Minimalist Sofa', icon: <SofaIcon /> },
{ id: 70010117, parentId: 70000101, name: 'Vintage Sofa', icon: <Hotel /> },
{ id: 70010118, parentId: 70000101, name: 'Modern Sofa', icon: <SofaIcon /> },
{ id: 70010119, parentId: 70000101, name: 'Classic Sofa', icon: <Hotel /> },
{ id: 70010120, parentId: 70000101, name: 'Outdoor Sofa', icon: <OutdoorGrill /> },
{ id: 70010121, parentId: 70000101, name: 'Wooden Frame Sofa', icon: <GridOn /> },
{ id: 70010122, parentId: 70000101, name: 'Metal Frame Sofa', icon: <Build /> },

/* ================= COFFEE TABLES ================= */

{ id: 70010201, parentId: 70000102, name: 'Wooden Coffee Table', icon: <Coffee /> },
{ id: 70010202, parentId: 70000102, name: 'Glass Coffee Table', icon: <Coffee /> },
{ id: 70010203, parentId: 70000102, name: 'Marble Coffee Table', icon: <Coffee /> },
{ id: 70010204, parentId: 70000102, name: 'Metal Coffee Table', icon: <Build /> },
{ id: 70010205, parentId: 70000102, name: 'Round Coffee Table', icon: <Coffee /> },
{ id: 70010206, parentId: 70000102, name: 'Square Coffee Table', icon: <Coffee /> },
{ id: 70010207, parentId: 70000102, name: 'Rectangular Coffee Table', icon: <Coffee /> },
{ id: 70010208, parentId: 70000102, name: 'Nested Coffee Table Set', icon: <GridOn /> },
{ id: 70010209, parentId: 70000102, name: 'Storage Coffee Table', icon: <GridOn /> },
{ id: 70010210, parentId: 70000102, name: 'Lift-top Coffee Table', icon: <Build /> },
{ id: 70010211, parentId: 70000102, name: 'Rattan Coffee Table', icon: <OutdoorGrill /> },
{ id: 70010212, parentId: 70000102, name: 'Industrial Coffee Table', icon: <Build /> },
{ id: 70010213, parentId: 70000102, name: 'Minimalist Coffee Table', icon: <Coffee /> },
{ id: 70010214, parentId: 70000102, name: 'Vintage Coffee Table', icon: <Hotel /> },
{ id: 70010215, parentId: 70000102, name: 'Luxury Gold-Trim Coffee Table', icon: <FormatPaint /> },

/* ================= TV STANDS ================= */

{ id: 70010301, parentId: 70000103, name: 'Modern TV Stand', icon: <Tv /> },
{ id: 70010302, parentId: 70000103, name: 'Classic TV Stand', icon: <Tv /> },
{ id: 70010303, parentId: 70000103, name: 'Wall-Mounted TV Unit', icon: <GridOn /> },
{ id: 70010304, parentId: 70000103, name: 'Floating TV Stand', icon: <Tv /> },
{ id: 70010305, parentId: 70000103, name: 'TV Stand with Storage Cabinets', icon: <GridOn /> },
{ id: 70010306, parentId: 70000103, name: 'TV Stand with Drawers', icon: <GridOn /> },
{ id: 70010307, parentId: 70000103, name: 'Glass TV Stand', icon: <Tv /> },
{ id: 70010308, parentId: 70000103, name: 'Wooden TV Stand', icon: <GridOn /> },
{ id: 70010309, parentId: 70000103, name: 'Metal Frame TV Stand', icon: <Build /> },
{ id: 70010310, parentId: 70000103, name: 'Industrial TV Stand', icon: <Build /> },
{ id: 70010311, parentId: 70000103, name: 'Luxury Marble-Top TV Stand', icon: <FormatPaint /> },
{ id: 70010312, parentId: 70000103, name: 'Minimalist Low TV Console', icon: <Tv /> },
{ id: 70010313, parentId: 70000103, name: 'Entertainment Unit', icon: <GridOn /> },
{ id: 70010314, parentId: 70000103, name: 'Corner TV Stand', icon: <Chair /> },
{ id: 70010315, parentId: 70000103, name: 'LED-Lit TV Stand', icon: <Light /> },

/* ================= DINING TABLES ================= */

{ id: 70030101, parentId: 70000301, name: 'Wooden Dining Table', icon: <TableRestaurant /> },
{ id: 70030102, parentId: 70000301, name: 'Glass Top Dining Table', icon: <TableRestaurant /> },
{ id: 70030103, parentId: 70000301, name: 'Marble Dining Table', icon: <TableRestaurant /> },
{ id: 70030104, parentId: 70000301, name: 'Metal Frame Dining Table', icon: <Build /> },
{ id: 70030105, parentId: 70000301, name: 'Round Dining Table', icon: <TableRestaurant /> },
{ id: 70030106, parentId: 70000301, name: 'Square Dining Table', icon: <TableRestaurant /> },
{ id: 70030107, parentId: 70000301, name: 'Rectangular Dining Table', icon: <TableRestaurant /> },
{ id: 70030108, parentId: 70000301, name: 'Extendable Dining Table', icon: <GridOn /> },
{ id: 70030109, parentId: 70000301, name: 'Folding Dining Table', icon: <Build /> },
{ id: 70030110, parentId: 70000301, name: '6-Seater Dining Table', icon: <TableRestaurant /> },
{ id: 70030111, parentId: 70000301, name: '8-Seater Dining Table', icon: <TableRestaurant /> },
{ id: 70030112, parentId: 70000301, name: '10-Seater Dining Table', icon: <TableRestaurant /> },
{ id: 70030113, parentId: 70000301, name: 'Industrial Dining Table', icon: <Build /> },
{ id: 70030114, parentId: 70000301, name: 'Minimalist Dining Table', icon: <TableRestaurant /> },
{ id: 70030115, parentId: 70000301, name: 'Luxury Gold-Trim Dining Table', icon: <FormatPaint /> },
{ id: 70030116, parentId: 70000301, name: 'Farmhouse Dining Table', icon: <GridOn /> },
{ id: 70030117, parentId: 70000301, name: 'Rattan Dining Table', icon: <OutdoorGrill /> },
{ id: 70030118, parentId: 70000301, name: 'Compact / Space-Saving Table', icon: <GridOn /> },

/* ================= DESKS ================= */

{ id: 70040101, parentId: 70000401, name: 'Executive Desk', icon: <Desk /> },
{ id: 70040102, parentId: 70000401, name: 'Computer Desk', icon: <Desk /> },
{ id: 70040103, parentId: 70000401, name: 'Writing Desk', icon: <Desk /> },
{ id: 70040104, parentId: 70000401, name: 'Study Desk', icon: <Desk /> },
{ id: 70040105, parentId: 70000401, name: 'Standing Desk', icon: <Build /> },
{ id: 70040106, parentId: 70000401, name: 'Adjustable Height Desk', icon: <Build /> },
{ id: 70040107, parentId: 70000401, name: 'L-Shaped Desk', icon: <GridOn /> },
{ id: 70040108, parentId: 70000401, name: 'U-Shaped Desk', icon: <GridOn /> },
{ id: 70040109, parentId: 70000401, name: 'Corner Desk', icon: <GridOn /> },
{ id: 70040110, parentId: 70000401, name: 'Minimalist Desk', icon: <Desk /> },
{ id: 70040111, parentId: 70000401, name: 'Industrial Desk', icon: <Build /> },
{ id: 70040112, parentId: 70000401, name: 'Wooden Desk', icon: <GridOn /> },
{ id: 70040113, parentId: 70000401, name: 'Metal Frame Desk', icon: <Build /> },
{ id: 70040114, parentId: 70000401, name: 'Glass Top Desk', icon: <Desk /> },
{ id: 70040115, parentId: 70000401, name: 'Desk with Storage Drawers', icon: <GridOn /> },
{ id: 70040116, parentId: 70000401, name: 'Desk with Hutch', icon: <GridOn /> },
{ id: 70040117, parentId: 70000401, name: 'Foldable Desk', icon: <Build /> },
{ id: 70040118, parentId: 70000401, name: 'Floating Wall-Mounted Desk', icon: <Desk /> },
{ id: 70040119, parentId: 70000401, name: 'Gaming Desk', icon: <Hotel /> },
{ id: 70040120, parentId: 70000401, name: 'Ergonomic Curved Desk', icon: <Desk /> },

/* ================= OUTDOOR SOFAS ================= */

{ id: 70050101, parentId: 70000501, name: 'Rattan Outdoor Sofa', icon: <Weekend /> },
{ id: 70050102, parentId: 70000501, name: 'Teak Outdoor Sofa', icon: <Weekend /> },
{ id: 70050103, parentId: 70000501, name: 'Aluminium Outdoor Sofa', icon: <Build /> },
{ id: 70050104, parentId: 70000501, name: 'Wicker Outdoor Sofa', icon: <Weekend /> },
{ id: 70050105, parentId: 70000501, name: 'Outdoor Sectional Sofa', icon: <SofaIcon /> },
{ id: 70050106, parentId: 70000501, name: 'Outdoor Lounge Set', icon: <Weekend /> },
{ id: 70050107, parentId: 70000501, name: 'Outdoor Daybed', icon: <Hotel /> },
{ id: 70050108, parentId: 70000501, name: 'Outdoor Sofa with Cushions', icon: <Texture /> },
{ id: 70050109, parentId: 70000501, name: 'Weatherproof Outdoor Sofa', icon: <Build /> },
{ id: 70050110, parentId: 70000501, name: 'Foldable Outdoor Sofa', icon: <Build /> },
{ id: 70050111, parentId: 70000501, name: 'Portable Outdoor Sofa', icon: <Chair /> },
{ id: 70050112, parentId: 70000501, name: 'Outdoor Pallet Sofa', icon: <GridOn /> },
{ id: 70050113, parentId: 70000501, name: 'Outdoor Corner Sofa', icon: <SofaIcon /> },
{ id: 70050114, parentId: 70000501, name: 'Luxury Outdoor Lounge Set', icon: <FormatPaint /> },
{ id: 70050115, parentId: 70000501, name: 'Rope-Weave Outdoor Sofa', icon: <Texture /> },


/* =========================================================
   KITCHEN (70000601 / 70000602)
========================================================= */

// Kitchen Cabinets
{ id: 70060101, parentId: 70000601, name: 'Modular Kitchen Cabinet', icon: <Kitchen /> },
{ id: 70060102, parentId: 70000601, name: 'Base Kitchen Cabinet', icon: <Kitchen /> },
{ id: 70060103, parentId: 70000601, name: 'Wall-Mounted Kitchen Cabinet', icon: <Kitchen /> },
{ id: 70060104, parentId: 70000601, name: 'Tall Pantry Cabinet', icon: <GridOn /> },
{ id: 70060105, parentId: 70000601, name: 'Corner Kitchen Cabinet', icon: <GridOn /> },
{ id: 70060106, parentId: 70000601, name: 'Glass Door Kitchen Cabinet', icon: <Kitchen /> },
{ id: 70060107, parentId: 70000601, name: 'Open Shelf Kitchen Unit', icon: <GridOn /> },

// Kitchen Islands
{ id: 70060201, parentId: 70000602, name: 'Portable Kitchen Island', icon: <TableRestaurant /> },
{ id: 70060202, parentId: 70000602, name: 'Kitchen Island with Storage', icon: <GridOn /> },
{ id: 70060203, parentId: 70000602, name: 'Kitchen Island with Seating', icon: <Chair /> },
{ id: 70060204, parentId: 70000602, name: 'Rolling Kitchen Island', icon: <Build /> },
{ id: 70060205, parentId: 70000602, name: 'Marble Top Kitchen Island', icon: <FormatPaint /> },

/* =========================================================
   LIGHTING (70000701 / 70000702 / 70000703)
========================================================= */

// Ceiling Lights
{ id: 70070101, parentId: 70000701, name: 'Modern Ceiling Light', icon: <Light /> },
{ id: 70070102, parentId: 70000701, name: 'Chandelier', icon: <Light /> },
{ id: 70070103, parentId: 70000701, name: 'Pendant Light', icon: <Light /> },
{ id: 70070104, parentId: 70000701, name: 'Flush Mount Light', icon: <Light /> },
{ id: 70070105, parentId: 70000701, name: 'LED Panel Light', icon: <Light /> },

// Floor Lamps
{ id: 70070201, parentId: 70000702, name: 'LED Floor Lamp', icon: <Light /> },
{ id: 70070202, parentId: 70000702, name: 'Arc Floor Lamp', icon: <Light /> },
{ id: 70070203, parentId: 70000702, name: 'Tripod Floor Lamp', icon: <Light /> },
{ id: 70070204, parentId: 70000702, name: 'Smart Floor Lamp', icon: <Build /> },

// Table Lamps
{ id: 70070301, parentId: 70000703, name: 'Modern Table Lamp', icon: <Light /> },
{ id: 70070302, parentId: 70000703, name: 'Bedside Table Lamp', icon: <Light /> },
{ id: 70070303, parentId: 70000703, name: 'Touch Control Lamp', icon: <Build /> },
{ id: 70070304, parentId: 70000703, name: 'Ceramic Table Lamp', icon: <FormatPaint /> },



/* =========================================================
   DECOR (70000801 / 70000803)
========================================================= */

// Wall Art
{ id: 70080101, parentId: 70000801, name: 'Abstract Wall Art', icon: <FormatPaint /> },
{ id: 70080102, parentId: 70000801, name: 'Canvas Wall Art', icon: <FormatPaint /> },
{ id: 70080103, parentId: 70000801, name: 'Framed Wall Art', icon: <FilterFrames /> },
{ id: 70080104, parentId: 70000801, name: 'Metal Wall Art', icon: <Build /> },
{ id: 70080105, parentId: 70000801, name: 'Wall Art Set (Multi-Panel)', icon: <GridOn /> },

// Mirrors
{ id: 70080301, parentId: 70000803, name: 'Decorative Mirror', icon: <FilterFrames /> },
{ id: 70080302, parentId: 70000803, name: 'Round Mirror', icon: <FilterFrames /> },
{ id: 70080303, parentId: 70000803, name: 'Full-Length Mirror', icon: <FilterFrames /> },
{ id: 70080304, parentId: 70000803, name: 'LED Mirror', icon: <Light /> },
{ id: 70080305, parentId: 70000803, name: 'Wall-Mounted Mirror', icon: <FilterFrames /> },

/* =========================================================
   STORAGE (70000901 / 70000902)
========================================================= */

// Storage Cabinets
{ id: 70090101, parentId: 70000901, name: 'Wood Storage Cabinet', icon: <GridOn /> },
{ id: 70090102, parentId: 70000901, name: 'Metal Storage Cabinet', icon: <Build /> },
{ id: 70090103, parentId: 70000901, name: 'Glass Display Cabinet', icon: <FilterFrames /> },
{ id: 70090104, parentId: 70000901, name: 'Lockable Storage Cabinet', icon: <Build /> },
{ id: 70090105, parentId: 70000901, name: 'Multi-Drawer Storage Unit', icon: <GridOn /> },

// Shoe Racks
{ id: 70090201, parentId: 70000902, name: 'Metal Shoe Rack', icon: <GridOn /> },
{ id: 70090202, parentId: 70000902, name: 'Wooden Shoe Rack', icon: <GridOn /> },
{ id: 70090203, parentId: 70000902, name: 'Shoe Cabinet with Doors', icon: <Build /> },
{ id: 70090204, parentId: 70000902, name: 'Stackable Shoe Rack', icon: <GridOn /> },

/* =========================================================
   KIDS (70001001 / 70001003)
========================================================= */

// Kids Beds
{ id: 70100101, parentId: 70001001, name: 'Bunk Bed', icon: <KingBed /> },
{ id: 70100102, parentId: 70001001, name: 'Loft Bed', icon: <Bed /> },
{ id: 70100103, parentId: 70001001, name: 'Toddler Bed', icon: <Bed /> },
{ id: 70100104, parentId: 70001001, name: 'House Frame Kids Bed', icon: <Build /> },

// Kids Storage
{ id: 70100301, parentId: 70001003, name: 'Kids Toy Organizer', icon: <GridOn /> },
{ id: 70100302, parentId: 70001003, name: 'Kids Bookshelf', icon: <GridOn /> },
{ id: 70100303, parentId: 70001003, name: 'Kids Storage Bench', icon: <Chair /> },
{ id: 70100304, parentId: 70001003, name: 'Toy Chest', icon: <GridOn /> },

/* =========================================================
   CUSTOM (70001101 / 70001102)
========================================================= */

// Custom Sofas
{ id: 70110101, parentId: 70001101, name: 'Made-to-Measure Sofa', icon: <SofaIcon /> },
{ id: 70110102, parentId: 70001101, name: 'Custom Sectional Sofa', icon: <SofaIcon /> },
{ id: 70110103, parentId: 70001101, name: 'Custom Recliner Sofa', icon: <Build /> },

// Custom Wardrobes
{ id: 70110201, parentId: 70001102, name: 'Custom Walk-in Wardrobe', icon: <Build /> },
{ id: 70110202, parentId: 70001102, name: 'Built-in Sliding Wardrobe', icon: <Carpenter /> },
{ id: 70110203, parentId: 70001102, name: 'Custom Closet System', icon: <GridOn /> },

];